import { useCallback, useEffect, useId, useLayoutEffect, useState, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { findVisibleTourAnchor, type ResolvedTourStep } from "@/components/product-tour/tourSteps";
import { Button } from "@/components/ui/button";

const HIGHLIGHT_PAD = 8;
const POPOVER_WIDTH = 288;
const POPOVER_GAP = 12;
const VIEWPORT_PAD = 16;
const POPOVER_ESTIMATED_HEIGHT = 180;

type HighlightRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type PopoverPos = {
  top: number;
  left: number;
};

type SpotlightTourProps = {
  steps: readonly ResolvedTourStep[];
  stepIndex: number;
  onBack: () => void;
  onNext: () => void;
  onDone: () => void;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function positionPopover(
  target: HighlightRect,
  viewport: { width: number; height: number },
): PopoverPos {
  const spaceRight = viewport.width - (target.left + target.width) - VIEWPORT_PAD;
  const spaceLeft = target.left - VIEWPORT_PAD;
  const spaceBottom = viewport.height - (target.top + target.height) - VIEWPORT_PAD;
  const spaceTop = target.top - VIEWPORT_PAD;
  const maxLeft = Math.max(VIEWPORT_PAD, viewport.width - POPOVER_WIDTH - VIEWPORT_PAD);
  const maxTop = Math.max(VIEWPORT_PAD, viewport.height - POPOVER_ESTIMATED_HEIGHT - VIEWPORT_PAD);

  if (spaceRight >= POPOVER_WIDTH + POPOVER_GAP) {
    return {
      left: target.left + target.width + POPOVER_GAP,
      top: clamp(target.top, VIEWPORT_PAD, maxTop),
    };
  }
  if (spaceLeft >= POPOVER_WIDTH + POPOVER_GAP) {
    return {
      left: target.left - POPOVER_WIDTH - POPOVER_GAP,
      top: clamp(target.top, VIEWPORT_PAD, maxTop),
    };
  }
  if (spaceBottom >= POPOVER_ESTIMATED_HEIGHT + POPOVER_GAP) {
    return {
      top: target.top + target.height + POPOVER_GAP,
      left: clamp(target.left, VIEWPORT_PAD, maxLeft),
    };
  }
  if (spaceTop >= POPOVER_ESTIMATED_HEIGHT + POPOVER_GAP) {
    return {
      top: target.top - POPOVER_ESTIMATED_HEIGHT - POPOVER_GAP,
      left: clamp(target.left, VIEWPORT_PAD, maxLeft),
    };
  }
  return {
    left: clamp((viewport.width - POPOVER_WIDTH) / 2, VIEWPORT_PAD, maxLeft),
    top: clamp(target.top + target.height + POPOVER_GAP, VIEWPORT_PAD, maxTop),
  };
}

export function SpotlightTour({
  steps,
  stepIndex,
  onBack,
  onNext,
  onDone,
}: SpotlightTourProps): ReactElement | null {
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const step = steps[stepIndex];
  const [highlight, setHighlight] = useState<HighlightRect | null>(null);
  const [popover, setPopover] = useState<PopoverPos | null>(null);

  const updatePosition = useCallback((): void => {
    if (!step) {
      setHighlight(null);
      setPopover(null);
      return;
    }
    const el = findVisibleTourAnchor(step.anchor);
    if (!el) {
      setHighlight(null);
      setPopover(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    const nextHighlight: HighlightRect = {
      top: rect.top - HIGHLIGHT_PAD,
      left: rect.left - HIGHLIGHT_PAD,
      width: rect.width + HIGHLIGHT_PAD * 2,
      height: rect.height + HIGHLIGHT_PAD * 2,
    };
    setHighlight(nextHighlight);
    setPopover(
      positionPopover(nextHighlight, { width: window.innerWidth, height: window.innerHeight }),
    );
  }, [step]);

  useLayoutEffect(() => {
    if (!step) return;
    const el = findVisibleTourAnchor(step.anchor);
    el?.scrollIntoView({ block: "nearest", inline: "nearest" });
    const frame = requestAnimationFrame(() => {
      updatePosition();
    });
    return () => cancelAnimationFrame(frame);
  }, [step, updatePosition]);

  useEffect(() => {
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [updatePosition]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onDone();
        return;
      }
      if (event.key === "ArrowRight" || event.key === "Enter") {
        event.preventDefault();
        if (stepIndex >= steps.length - 1) onDone();
        else onNext();
        return;
      }
      if (event.key === "ArrowLeft" && stepIndex > 0) {
        event.preventDefault();
        onBack();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onBack, onDone, onNext, stepIndex, steps.length]);

  if (!step || typeof document === "undefined") return null;

  const isLast = stepIndex >= steps.length - 1;
  const isFirst = stepIndex <= 0;

  return createPortal(
    <div className="fixed inset-0 z-[80]" role="presentation">
      <div className="absolute inset-0 bg-black/10" aria-hidden />
      {highlight ? (
        <div
          className="pointer-events-none absolute rounded-xl ring-2 ring-primary shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
          style={{
            top: highlight.top,
            left: highlight.left,
            width: highlight.width,
            height: highlight.height,
          }}
          aria-hidden
        />
      ) : null}
      {popover ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          className="absolute z-10 flex w-72 flex-col gap-3 rounded-xl bg-popover p-4 text-sm text-popover-foreground shadow-md ring-1 ring-foreground/10"
          style={{ top: popover.top, left: popover.left, width: POPOVER_WIDTH }}
        >
          <p className="text-xs font-medium text-muted-foreground">
            {t("productTour.stepOf", { current: stepIndex + 1, total: steps.length })}
          </p>
          <div className="flex flex-col gap-1">
            <h2 id={titleId} className="font-medium leading-snug">
              {t(step.titleKey)}
            </h2>
            <p id={descriptionId} className="text-muted-foreground">
              {t(step.descriptionKey)}
            </p>
          </div>
          <div className="flex items-center justify-end gap-2">
            {isFirst ? null : (
              <Button type="button" variant="ghost" size="sm" onClick={onBack}>
                {t("productTour.back")}
              </Button>
            )}
            {isLast ? (
              <Button type="button" size="sm" autoFocus onClick={onDone}>
                {t("productTour.done")}
              </Button>
            ) : (
              <Button type="button" size="sm" autoFocus onClick={onNext}>
                {t("productTour.next")}
              </Button>
            )}
          </div>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
